import van, {type State} from "vanjs-core"
import {Net} from "@utils/net";
import {VanUtil as vu} from "@utils/vanUtil";
import {VanComponent} from "@utils/vanComponent";
import {Busy} from "@commons/layout/busy";
import {TrimState} from "@utils/wrapper";
import {StringUtil} from "@utils/stringUtil";
import type {HqLoginPage} from "@hq/login/hqLoginPage";
import type {Alert} from "@commons/components/bootstrap/alert";
import type {HqLoginPageResponse} from "hq-dto";
import type {HqLoginResponse} from "hq-dto";

const { div, form, button, h2, h6, label, input, progress } = van.tags;

type FormType =
{
    userName: TrimState,
    password: State<string>,
    returnUrl: State<string|undefined>,
};

export class HqLoginPanel extends VanComponent
{
    #parent: HqLoginPage; 
    #alert: Alert;
    #progress: HTMLElement|undefined;
    #model: HqLoginPageResponse|undefined;
    form: FormType;

    constructor(parent: HqLoginPage, alert: Alert)
    {
        super();
        
        this.#parent = parent;
        this.#alert = alert;
        
        this.form = 
        {
            userName: new TrimState(),
            password: van.state(''),
            returnUrl: van.state(),
        };

        this.#progress = Busy.visibility(progress({ "aria-label": "Submitting form" }));
    }

    set model(value: HqLoginPageResponse)
    {
        this.#model = value;
        this.form.userName.val = value.userName ?? '';
        this.form.returnUrl.val = value.returnUrl;
    }
    
    get model(): HqLoginPageResponse|undefined
    {
        return this.#model;
    }

    toDom(): HTMLElement
    {
        return div({"class": "card", "style": "padding: 30px 30px; background-color: #edf9f3", 'data-van-component':  StringUtil.camelToKebab(this.constructor.name)},
            div({"class": "card-body"},
                h2({"class": "card-title fw-bold"}, 'Sign In'),
                div({"class": "card-text"},
                    form({ onsubmit: event => this.submit(event) },
                        div({class: "mt-3"},
                            label({class: "d-block"}, h6('Username')),
                            input({ class: 'form-control', type: 'text', required: 'required', autofocus: true,
                                value: () => this.form.userName.val ?? '', 
                                oninput: e => this.form.userName.val = e.target.value 
                            }),
                            vu.load(vu.focalOnLoad)
                        ),
                        div({class: "mt-2"},
                            label({class: "d-block"}, h6('Password')),
                            vu.password({ class: 'form-control', required: 'required', bind: this.form.password })
                        ),
                        div(button({ class: "btn btn-lg btn-primary mt-4", type: 'submit' }, 'Sign In')),
                        div({class: "mt-1"}, this.#progress),
                    )
                )
            )
        );
    }

    submit(event: Event): void
    {
        this.#alert.hide();
        event.preventDefault();     // stops browser from submitting form

        let busyCancel = Busy.instance.setBusy();
        Net
            .post<HqLoginResponse>({ url: '/login/login', data: this.form })
            .then((loginResponse: HqLoginResponse) =>
            {
                this.#parent.onLogin(loginResponse);
            })
            .finally(busyCancel);
    }
}
